import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ref, set, push, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MoonPhaseIcon from '../components/MoonPhaseIcon';
import { MOON_PHASES, getCurrentMoonPhase, getNextMoonPhaseDate } from '../utils/moonPhases';
import './Journal.css';

const Journal = () => {
  const { userProfile, currentUser } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState('menu'); // 'menu', 'poetry', 'letter', 'signature', 'seal', 'journalEntry', 'readJournal'
  const currentMoonPhase = getCurrentMoonPhase();
  const [journalEntries, setJournalEntries] = useState([]);
  const [currentEntryIndex, setCurrentEntryIndex] = useState(0);
  const [partnerName, setPartnerName] = useState('');
  // Check if user is Otto (auto-send all poems to 'otto' medium)
  const isOttoUser = currentUser?.email === 'otto@example.com' || userProfile?.name?.toLowerCase() === 'otto';

  const [newEntry, setNewEntry] = useState({
    title: '',
    content: '',
    destination: 'poetry', // 'poetry' or 'letters'
    medium: isOttoUser ? 'otto' : 'breath', // Poetry viewing style: 'breath', 'constellations', 'windBlown', 'rain', 'campfire', 'otto'
    sealMethod: 'moonPhase', // 'moonPhase', 'timeDelay', 'distance', 'weather'
    sealedMoonPhase: currentMoonPhase.name, // Moon phase when letter can be opened
    delayDays: 7, // Number of days for time delay seal
    distanceMiles: 100, // Distance in miles for distance-based seal
    weatherCondition: 'rainy', // Weather condition for weather-based seal
    signature: userProfile?.name || 'Anonymous', // How the user signs the letter
  });

  // Get partner's name or names
  useEffect(() => {
    if (!currentUser?.email) return;

    const usersRef = ref(database, 'users');
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const users = snapshot.val();
      console.log('All users from Firebase:', users);
      console.log('Current user email:', currentUser?.email);

      if (users) {
        const allUsers = Object.values(users);
        // Exclude current user, admin, and otto from partner names
        const otherUsers = allUsers.filter(user =>
          user.email !== currentUser?.email &&
          user.email !== 'admin@example.com' &&
          user.email !== 'otto@example.com' &&
          user.name?.toLowerCase() !== 'admin' &&
          user.name?.toLowerCase() !== 'otto'
        );

        console.log('Other users:', otherUsers);

        if (otherUsers.length === 1) {
          // One partner (Omi or Henry)
          setPartnerName(otherUsers[0].name || 'Love');
        } else if (otherUsers.length > 1) {
          // Multiple partners
          const names = otherUsers.map(u => u.name).filter(Boolean);
          setPartnerName(names.join(' and ') || 'Loves');
        } else {
          // No partners found - set a default
          setPartnerName('Love');
        }
      } else {
        // No users in database yet
        setPartnerName('Love');
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Load journal entries
  useEffect(() => {
    const entriesRef = ref(database, 'journal/entries');
    const unsubscribe = onValue(entriesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const entriesArray = Object.entries(data).map(([id, entry]) => ({
          id,
          ...entry,
        }));
        entriesArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setJournalEntries(entriesArray);
      } else {
        setJournalEntries([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleSaveEntry = async () => {
    if (!newEntry.content.trim()) {
      return;
    }

    try {
      let destination;
      if (currentPage === 'poetry') {
        destination = 'poetry/entries';
      } else if (currentPage === 'journalEntry') {
        destination = 'journal/entries';
      } else {
        destination = 'letters/entries';
      }

      const entriesRef = ref(database, destination);
      const newEntryRef = push(entriesRef);

      const entryData = {
        content: newEntry.content,
        author: userProfile?.name || currentUser?.email || 'anonymous',
        authorEmail: currentUser?.email || 'anonymous',
        createdAt: new Date().toISOString(),
      };

      // Add medium only for poetry entries
      if (currentPage === 'poetry') {
        entryData.medium = newEntry.medium;
        entryData.title = newEntry.title || 'Untitled';
      }

      // Add title for journal entries
      if (currentPage === 'journalEntry') {
        entryData.title = newEntry.title || 'Untitled';
      }

      // Add sealing for letters
      if (currentPage === 'seal') {
        entryData.sealMethod = newEntry.sealMethod;
        entryData.isSealed = true;
        entryData.signature = newEntry.signature; // Add signature to letter data

        // Add seal-specific data
        if (newEntry.sealMethod === 'moonPhase') {
          entryData.sealedMoonPhase = newEntry.sealedMoonPhase;
          const openDate = getNextMoonPhaseDate(newEntry.sealedMoonPhase);
          entryData.canOpenAt = openDate.toISOString();
        } else if (newEntry.sealMethod === 'timeDelay') {
          const openDate = new Date();
          openDate.setDate(openDate.getDate() + newEntry.delayDays);
          entryData.canOpenAt = openDate.toISOString();
          entryData.delayDays = newEntry.delayDays;
        } else if (newEntry.sealMethod === 'distance') {
          entryData.distanceMiles = newEntry.distanceMiles;
          entryData.canOpenAt = null; // Opens when distance threshold met
        } else if (newEntry.sealMethod === 'weather') {
          entryData.weatherCondition = newEntry.weatherCondition;
          entryData.canOpenAt = null; // Opens when weather condition met
        }
      }

      await set(newEntryRef, entryData);

      // Navigate to the destination page or reset form
      if (currentPage === 'poetry') {
        navigate('/poetry');
      } else if (currentPage === 'seal') {
        navigate('/letters');
      } else if (currentPage === 'journalEntry') {
        // Reset form and go back to menu
        setNewEntry({
          title: '',
          content: '',
          destination: 'poetry',
          medium: 'one',
          sealMethod: 'moonPhase',
          sealedMoonPhase: currentMoonPhase.name,
          delayDays: 7,
          distanceMiles: 100,
          weatherCondition: 'rainy',
        });
        setCurrentPage('menu');
      }
    } catch (error) {
      console.error('Error saving entry:', error);
    }
  };

  return (
    <div className="journal-page">
      {/* Decorative romantic elements scattered across the page */}
      <svg className="journal-decorations" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}>
        {/* Hearts scattered around - using viewBox coordinates 0-100 */}
        {/* Top left heart */}
        <path d="M 8 15 C 8 13, 9.5 12, 11 14 C 12.5 12, 14 13, 14 15 C 14 18, 11 21, 11 21 C 11 21, 8 18, 8 15" fill="none" stroke="#d4a5a5" strokeWidth="0.4" opacity="0.3" />
        {/* Top right heart */}
        <path d="M 88 12 C 88 10, 89.5 9, 91 11 C 92.5 9, 94 10, 94 12 C 94 15, 91 18, 91 18 C 91 18, 88 15, 88 12" fill="none" stroke="#c9a0a0" strokeWidth="0.35" opacity="0.25" />
        {/* Bottom left heart */}
        <path d="M 6 82 C 6 80, 7.5 79, 9 81 C 10.5 79, 12 80, 12 82 C 12 85, 9 88, 9 88 C 9 88, 6 85, 6 82" fill="none" stroke="#d4a5a5" strokeWidth="0.4" opacity="0.25" />
        {/* Bottom right heart */}
        <path d="M 90 78 C 90 76, 91.5 75, 93 77 C 94.5 75, 96 76, 96 78 C 96 81, 93 84, 93 84 C 93 84, 90 81, 90 78" fill="none" stroke="#c9a0a0" strokeWidth="0.35" opacity="0.22" />
        {/* Middle left heart */}
        <path d="M 4 45 C 4 43.5, 5 43, 6 44.5 C 7 43, 8 43.5, 8 45 C 8 47, 6 49, 6 49 C 6 49, 4 47, 4 45" fill="none" stroke="#d4a5a5" strokeWidth="0.3" opacity="0.2" />
        {/* Middle right heart */}
        <path d="M 93 50 C 93 48.5, 94 48, 95 49.5 C 96 48, 97 48.5, 97 50 C 97 52, 95 54, 95 54 C 95 54, 93 52, 93 50" fill="none" stroke="#c9a0a0" strokeWidth="0.3" opacity="0.2" />

        {/* Sparkles / small stars */}
        <g opacity="0.3" stroke="#a08060" strokeWidth="0.25" strokeLinecap="round">
          {/* Top area sparkle */}
          <path d="M 15 8 L 15 5 M 13.5 6.5 L 16.5 6.5" />
          {/* Right side sparkle */}
          <path d="M 92 35 L 92 32 M 90.5 33.5 L 93.5 33.5" />
          {/* Bottom left sparkle */}
          <path d="M 12 90 L 12 87 M 10.5 88.5 L 13.5 88.5" />
          {/* Left side sparkle */}
          <path d="M 5 60 L 5 57 M 3.5 58.5 L 6.5 58.5" />
          {/* Bottom right sparkle */}
          <path d="M 88 92 L 88 89 M 86.5 90.5 L 89.5 90.5" />
        </g>

        {/* Small dots scattered */}
        <circle cx="3" cy="25" r="0.5" fill="#c9a0a0" opacity="0.2" />
        <circle cx="97" cy="30" r="0.4" fill="#d4a5a5" opacity="0.18" />
        <circle cx="10" cy="70" r="0.45" fill="#c9a0a0" opacity="0.15" />
        <circle cx="95" cy="65" r="0.5" fill="#d4a5a5" opacity="0.18" />
        <circle cx="7" cy="95" r="0.4" fill="#c9a0a0" opacity="0.15" />
        <circle cx="92" cy="88" r="0.45" fill="#d4a5a5" opacity="0.15" />

        {/* Delicate curly flourishes */}
        <path d="M 2 35 Q 4 33, 3 30 Q 5 32, 4 35" fill="none" stroke="#8B7355" strokeWidth="0.25" opacity="0.2" />
        <path d="M 96 42 Q 98 40, 97 37 Q 99 39, 98 42" fill="none" stroke="#8B7355" strokeWidth="0.25" opacity="0.18" />
      </svg>

      {/* Journal Book */}
      <motion.div
        className={`journal-book ${(currentPage === 'letter' || currentPage === 'signature' || currentPage === 'seal') ? 'no-spine' : ''}`}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Book Cover/Spine - Only show for journal pages */}
        {currentPage !== 'letter' && currentPage !== 'signature' && currentPage !== 'seal' && (
          <div className="book-spine"></div>
        )}

        {/* Menu Page (Left side when opened) */}
        {currentPage === 'menu' && (
          <>
            <div className="journal-left-page">
              {/* Corner decorations for left page */}
              <svg className="page-corner-decor top-left" viewBox="0 0 50 50" style={{ position: 'absolute', top: '8px', left: '8px', width: '35px', height: '35px', opacity: 0.35 }}>
                <path d="M 8 25 C 8 20, 14 17, 20 22 C 26 17, 32 20, 32 25 C 32 32, 20 40, 20 40 C 20 40, 8 32, 8 25" fill="none" stroke="#c9a0a0" strokeWidth="1.5" />
                <path d="M 5 8 L 5 2 M 2 5 L 8 5" stroke="#8B7355" strokeWidth="1" strokeLinecap="round" opacity="0.6" />
              </svg>
              <svg className="page-corner-decor bottom-left" viewBox="0 0 50 50" style={{ position: 'absolute', bottom: '8px', left: '8px', width: '35px', height: '35px', opacity: 0.35 }}>
                <path d="M 5 42 L 5 36 M 2 39 L 8 39" stroke="#8B7355" strokeWidth="1" strokeLinecap="round" opacity="0.6" />
                <circle cx="25" cy="25" r="2" fill="#c9a0a0" opacity="0.5" />
                <path d="M 15 35 Q 20 30, 25 35 Q 30 30, 35 35" fill="none" stroke="#c9a0a0" strokeWidth="1" opacity="0.5" />
              </svg>
              <div className="page-content">
                <h2 className="journal-heading">Our Journal</h2>
                <p className="journal-intro">
                  This is where we write to each other.
                  <br />
                  Choose what you'd like to do:
                </p>

                <div className="menu-section" style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                  <motion.button
                    className="journal-menu-button close-journal-button"
                    onClick={() => navigate('/home')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="menu-label">Close Journal</span>
                    <p className="menu-description">Return to desk</p>
                  </motion.button>

                  <motion.button
                    className="journal-menu-button read-journal-button"
                    onClick={() => setCurrentPage('readJournal')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="menu-label">Read Journal</span>
                    <p className="menu-description">View our entries</p>
                  </motion.button>
                </div>

                {/* Cute couple holding hands illustration */}
                <motion.img
                  src="/journalIllustration.png"
                  alt="Couple holding hands"
                  className="journal-illustration"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.85 }}
                  transition={{ duration: 5, ease: "easeOut" }}
                />
              </div>
            </div>

            <div className="journal-right-page">
              {/* Corner decorations for right page */}
              <svg className="page-corner-decor top-right" viewBox="0 0 50 50" style={{ position: 'absolute', top: '8px', right: '8px', width: '35px', height: '35px', opacity: 0.35 }}>
                <path d="M 18 25 C 18 20, 24 17, 30 22 C 36 17, 42 20, 42 25 C 42 32, 30 40, 30 40 C 30 40, 18 32, 18 25" fill="none" stroke="#c9a0a0" strokeWidth="1.5" />
                <path d="M 45 8 L 45 2 M 42 5 L 48 5" stroke="#8B7355" strokeWidth="1" strokeLinecap="round" opacity="0.6" />
              </svg>
              <svg className="page-corner-decor bottom-right" viewBox="0 0 50 50" style={{ position: 'absolute', bottom: '8px', right: '8px', width: '35px', height: '35px', opacity: 0.35 }}>
                <path d="M 45 42 L 45 36 M 42 39 L 48 39" stroke="#8B7355" strokeWidth="1" strokeLinecap="round" opacity="0.6" />
                <circle cx="25" cy="25" r="2" fill="#c9a0a0" opacity="0.5" />
                <path d="M 15 35 Q 20 30, 25 35 Q 30 30, 35 35" fill="none" stroke="#c9a0a0" strokeWidth="1" opacity="0.5" />
              </svg>
              <div className="page-content menu-options">
                <p className="menu-flavor-text left">our souls wrote their way...</p>
                <motion.button
                  className="journal-menu-button"
                  onClick={() => setCurrentPage('poetry')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="menu-label">Write Poetry</span>
                  <p className="menu-description">Connect with art, express yourself</p>
                </motion.button>

                <motion.button
                  className="journal-menu-button"
                  onClick={() => setCurrentPage('letter')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="menu-label">Write a Letter</span>
                  <p className="menu-description">Call out with a bird song</p>
                </motion.button>

                <motion.button
                  className="journal-menu-button"
                  onClick={() => setCurrentPage('journalEntry')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="menu-label">Write Journal Entry</span>
                  <p className="menu-description">thoughts & feelings</p>
                </motion.button>
                <p className="menu-flavor-text right">...into each other's lives</p>
              </div>
            </div>
          </>
        )}

        {/* Poetry Writing Page */}
        {currentPage === 'poetry' && (
          <>
            <div className="journal-left-page">
              <div className="page-content">
                <button className="back-button" onClick={() => setCurrentPage('menu')}>
                  ← Back
                </button>
                <h3 className="page-title">Writing Poetry</h3>
                <div className="writing-options">
                  <label className="option-label">Title:</label>
                  <input
                    type="text"
                    placeholder="Poem title..."
                    value={newEntry.title}
                    onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })}
                    className="title-input"
                  />

                  {!isOttoUser && (
                    <>
                      <label className="option-label">Medium:</label>
                      <div className="style-buttons">
                        <button
                          className={`style-btn ${newEntry.medium === 'breath' ? 'active' : ''}`}
                          onClick={() => setNewEntry({ ...newEntry, medium: 'breath' })}
                        >
                          Whisper these Words
                        </button>
                        <button
                          className={`style-btn ${newEntry.medium === 'constellations' ? 'active' : ''}`}
                          onClick={() => setNewEntry({ ...newEntry, medium: 'constellations' })}
                        >
                          Cast upon the Night Sky
                        </button>
                        <button
                          className={`style-btn ${newEntry.medium === 'windBlown' ? 'active' : ''}`}
                          onClick={() => setNewEntry({ ...newEntry, medium: 'windBlown' })}
                        >
                          Set Free into the Breeze
                        </button>
                        <button
                          className={`style-btn ${newEntry.medium === 'rain' ? 'active' : ''}`}
                          onClick={() => setNewEntry({ ...newEntry, medium: 'rain' })}
                        >
                          Send as Kisses from the Sky
                        </button>
                        <button
                          className={`style-btn ${newEntry.medium === 'campfire' ? 'active' : ''}`}
                          onClick={() => setNewEntry({ ...newEntry, medium: 'campfire' })}
                        >
                          Share by the Campfire
                        </button>
                      </div>
                    </>
                  )}
                  {isOttoUser && (
                    <p className="otto-auto-send-message">
                      All poems will be sent to Otto's view automatically 🍊
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="journal-right-page">
              <div className="page-content">
                <textarea
                  placeholder="Write your poem here..."
                  value={newEntry.content}
                  onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
                  className="journal-textarea"
                />
                <button className="send-button" onClick={handleSaveEntry}>
                  Send this Poem →
                </button>
              </div>
            </div>
          </>
        )}

        {/* Letter Writing Page */}
        {currentPage === 'letter' && (
          <div className="letter-writing-container parchment-bg">
            {/* ===== CORNER VINES WITH FLOWERS ===== */}
            {/* Top-left vine corner */}
            <svg viewBox="0 0 150 150" style={{ position: 'absolute', top: '10px', left: '10px', width: '100px', height: '100px', opacity: 0.3 }}>
              <path d="M 5 80 Q 5 40, 25 25 Q 40 10, 80 5" fill="none" stroke="#5C4033" strokeWidth="2" />
              <path d="M 10 70 Q 15 50, 30 35 Q 45 20, 70 15" fill="none" stroke="#6B5344" strokeWidth="1.5" />
              {/* Leaves */}
              <ellipse cx="20" cy="55" rx="8" ry="4" fill="#7a9a6a" transform="rotate(-45 20 55)" />
              <ellipse cx="35" cy="35" rx="7" ry="3.5" fill="#8aaa7a" transform="rotate(-30 35 35)" />
              <ellipse cx="55" cy="22" rx="6" ry="3" fill="#7a9a6a" transform="rotate(-15 55 22)" />
              {/* Small flower */}
              <circle cx="45" cy="45" r="5" fill="#d4a0a0" opacity="0.7" />
              <circle cx="45" cy="45" r="2" fill="#c4877a" />
            </svg>
            {/* Top-right vine corner */}
            <svg viewBox="0 0 150 150" style={{ position: 'absolute', top: '10px', right: '10px', width: '100px', height: '100px', opacity: 0.3, transform: 'scaleX(-1)' }}>
              <path d="M 5 80 Q 5 40, 25 25 Q 40 10, 80 5" fill="none" stroke="#5C4033" strokeWidth="2" />
              <path d="M 10 70 Q 15 50, 30 35 Q 45 20, 70 15" fill="none" stroke="#6B5344" strokeWidth="1.5" />
              <ellipse cx="20" cy="55" rx="8" ry="4" fill="#7a9a6a" transform="rotate(-45 20 55)" />
              <ellipse cx="35" cy="35" rx="7" ry="3.5" fill="#8aaa7a" transform="rotate(-30 35 35)" />
              <ellipse cx="55" cy="22" rx="6" ry="3" fill="#7a9a6a" transform="rotate(-15 55 22)" />
              <circle cx="28" cy="50" r="4" fill="#b8a0c4" opacity="0.7" />
              <circle cx="28" cy="50" r="1.5" fill="#9a7aaa" />
            </svg>
            {/* Bottom-left vine corner */}
            <svg viewBox="0 0 150 150" style={{ position: 'absolute', bottom: '10px', left: '10px', width: '100px', height: '100px', opacity: 0.3, transform: 'scaleY(-1)' }}>
              <path d="M 5 80 Q 5 40, 25 25 Q 40 10, 80 5" fill="none" stroke="#5C4033" strokeWidth="2" />
              <path d="M 10 70 Q 15 50, 30 35 Q 45 20, 70 15" fill="none" stroke="#6B5344" strokeWidth="1.5" />
              <ellipse cx="20" cy="55" rx="8" ry="4" fill="#7a9a6a" transform="rotate(-45 20 55)" />
              <ellipse cx="35" cy="35" rx="7" ry="3.5" fill="#8aaa7a" transform="rotate(-30 35 35)" />
              <ellipse cx="55" cy="22" rx="6" ry="3" fill="#7a9a6a" transform="rotate(-15 55 22)" />
              <circle cx="50" cy="35" r="4.5" fill="#d4a0a0" opacity="0.7" />
              <circle cx="50" cy="35" r="1.8" fill="#c4877a" />
            </svg>
            {/* Bottom-right vine corner */}
            <svg viewBox="0 0 150 150" style={{ position: 'absolute', bottom: '10px', right: '10px', width: '100px', height: '100px', opacity: 0.3, transform: 'scale(-1)' }}>
              <path d="M 5 80 Q 5 40, 25 25 Q 40 10, 80 5" fill="none" stroke="#5C4033" strokeWidth="2" />
              <path d="M 10 70 Q 15 50, 30 35 Q 45 20, 70 15" fill="none" stroke="#6B5344" strokeWidth="1.5" />
              <ellipse cx="20" cy="55" rx="8" ry="4" fill="#7a9a6a" transform="rotate(-45 20 55)" />
              <ellipse cx="35" cy="35" rx="7" ry="3.5" fill="#8aaa7a" transform="rotate(-30 35 35)" />
              <ellipse cx="55" cy="22" rx="6" ry="3" fill="#7a9a6a" transform="rotate(-15 55 22)" />
              <circle cx="40" cy="28" r="4" fill="#a0c4b8" opacity="0.7" />
              <circle cx="40" cy="28" r="1.5" fill="#7aaa9a" />
            </svg>

            {/* ===== BIRD ON BRANCH - Top right ===== */}
            <svg viewBox="0 0 120 80" style={{ position: 'absolute', top: '15%', right: '8%', width: '80px', height: '55px', opacity: 0.25 }}>
              {/* Branch */}
              <path d="M 0 50 Q 30 45, 60 50 Q 90 48, 120 55" fill="none" stroke="#5C4033" strokeWidth="3" />
              <path d="M 40 50 Q 35 40, 25 35" fill="none" stroke="#5C4033" strokeWidth="1.5" />
              <path d="M 80 49 Q 85 38, 95 32" fill="none" stroke="#5C4033" strokeWidth="1.5" />
              {/* Leaves on branch */}
              <ellipse cx="25" cy="33" rx="6" ry="3" fill="#7a9a6a" transform="rotate(-30 25 33)" />
              <ellipse cx="95" cy="30" rx="5" ry="2.5" fill="#8aaa7a" transform="rotate(20 95 30)" />
              {/* Bird body */}
              <ellipse cx="65" cy="38" rx="12" ry="9" fill="#6B5344" />
              {/* Bird head */}
              <circle cx="78" cy="32" r="7" fill="#6B5344" />
              {/* Beak */}
              <path d="M 85 32 L 92 33 L 85 35 Z" fill="#c4a060" />
              {/* Eye */}
              <circle cx="80" cy="30" r="2" fill="#1a0f08" />
              <circle cx="81" cy="29" r="0.8" fill="white" />
              {/* Wing */}
              <ellipse cx="62" cy="38" rx="8" ry="6" fill="#5C4033" />
              {/* Tail */}
              <path d="M 53 38 L 42 35 L 44 40 L 40 42 L 53 42 Z" fill="#5C4033" />
              {/* Legs */}
              <path d="M 65 47 L 65 52 M 63 52 L 67 52" stroke="#5C4033" strokeWidth="1.5" />
              <path d="M 70 47 L 70 51 M 68 51 L 72 51" stroke="#5C4033" strokeWidth="1.5" />
            </svg>

            {/* ===== BIRD ON BRANCH - Bottom left ===== */}
            <svg viewBox="0 0 100 70" style={{ position: 'absolute', bottom: '18%', left: '5%', width: '70px', height: '50px', opacity: 0.2 }}>
              {/* Branch */}
              <path d="M 0 40 Q 25 35, 50 38 Q 75 36, 100 42" fill="none" stroke="#5C4033" strokeWidth="2.5" />
              <path d="M 60 37 Q 65 28, 72 22" fill="none" stroke="#5C4033" strokeWidth="1.2" />
              <ellipse cx="72" cy="20" rx="5" ry="2.5" fill="#7a9a6a" transform="rotate(15 72 20)" />
              {/* Small bird */}
              <ellipse cx="35" cy="28" rx="9" ry="7" fill="#7a6a5a" />
              <circle cx="44" cy="24" r="5.5" fill="#7a6a5a" />
              <path d="M 49 24 L 54 25 L 49 27 Z" fill="#c4a060" />
              <circle cx="46" cy="22" r="1.5" fill="#1a0f08" />
              <ellipse cx="33" cy="28" rx="6" ry="4.5" fill="#6a5a4a" />
              <path d="M 26 28 L 18 26 L 20 30 L 16 32 L 26 31 Z" fill="#6a5a4a" />
              <path d="M 35 35 L 35 39 M 33 39 L 37 39" stroke="#5C4033" strokeWidth="1.2" />
            </svg>

            {/* ===== SPARKLES ===== */}
            {/* Large sparkles */}
            <svg style={{ position: 'absolute', top: '20%', left: '18%', width: '20px', height: '20px', opacity: 0.35 }}>
              <path d="M 10 0 L 10 20 M 0 10 L 20 10 M 3 3 L 17 17 M 17 3 L 3 17" stroke="#c4a060" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', top: '35%', right: '12%', width: '18px', height: '18px', opacity: 0.3 }}>
              <path d="M 9 0 L 9 18 M 0 9 L 18 9 M 2.5 2.5 L 15.5 15.5 M 15.5 2.5 L 2.5 15.5" stroke="#d4a0a0" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', bottom: '40%', left: '8%', width: '16px', height: '16px', opacity: 0.28 }}>
              <path d="M 8 0 L 8 16 M 0 8 L 16 8 M 2 2 L 14 14 M 14 2 L 2 14" stroke="#b8a0c4" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', top: '55%', right: '6%', width: '15px', height: '15px', opacity: 0.25 }}>
              <path d="M 7.5 0 L 7.5 15 M 0 7.5 L 15 7.5 M 2 2 L 13 13 M 13 2 L 2 13" stroke="#c4a060" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            {/* Small sparkles (4-point stars) */}
            <svg style={{ position: 'absolute', top: '12%', left: '35%', width: '12px', height: '12px', opacity: 0.3 }}>
              <path d="M 6 0 L 6 12 M 0 6 L 12 6" stroke="#d4b896" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', top: '45%', left: '15%', width: '10px', height: '10px', opacity: 0.25 }}>
              <path d="M 5 0 L 5 10 M 0 5 L 10 5" stroke="#c4a0a0" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', bottom: '25%', right: '18%', width: '11px', height: '11px', opacity: 0.28 }}>
              <path d="M 5.5 0 L 5.5 11 M 0 5.5 L 11 5.5" stroke="#a0b4c4" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', top: '28%', right: '25%', width: '9px', height: '9px', opacity: 0.22 }}>
              <path d="M 4.5 0 L 4.5 9 M 0 4.5 L 9 4.5" stroke="#d4a0a0" strokeWidth="1" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', bottom: '55%', left: '25%', width: '10px', height: '10px', opacity: 0.26 }}>
              <path d="M 5 0 L 5 10 M 0 5 L 10 5" stroke="#c4c4a0" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', top: '65%', right: '22%', width: '8px', height: '8px', opacity: 0.2 }}>
              <path d="M 4 0 L 4 8 M 0 4 L 8 4" stroke="#b8a0c4" strokeWidth="1" strokeLinecap="round" />
            </svg>

            {/* ===== HEARTS scattered ===== */}
            <svg style={{ position: 'absolute', top: '30%', right: '5%', width: '22px', height: '22px', opacity: 0.18 }}>
              <path d="M 11 5 C 11 2, 14 0.5, 16.5 3 C 19 0.5, 22 2, 22 5 C 22 9, 16.5 14, 16.5 14 C 16.5 14, 11 9, 11 5" fill="#8B4545" />
            </svg>
            <svg style={{ position: 'absolute', bottom: '35%', right: '4%', width: '16px', height: '16px', opacity: 0.14 }}>
              <path d="M 8 3.5 C 8 1.5, 10 0.5, 12 2.5 C 14 0.5, 16 1.5, 16 3.5 C 16 6.5, 12 10, 12 10 C 12 10, 8 6.5, 8 3.5" fill="#9B5555" />
            </svg>
            <svg style={{ position: 'absolute', top: '50%', left: '4%', width: '14px', height: '14px', opacity: 0.12 }}>
              <path d="M 7 3 C 7 1.2, 8.8 0.4, 10.5 2 C 12.2 0.4, 14 1.2, 14 3 C 14 5.5, 10.5 8.5, 10.5 8.5 C 10.5 8.5, 7 5.5, 7 3" fill="#8B4545" />
            </svg>
            <svg style={{ position: 'absolute', bottom: '20%', left: '22%', width: '12px', height: '12px', opacity: 0.1 }}>
              <path d="M 6 2.5 C 6 1, 7.5 0.3, 9 1.7 C 10.5 0.3, 12 1, 12 2.5 C 12 4.5, 9 7, 9 7 C 9 7, 6 4.5, 6 2.5" fill="#9B5555" />
            </svg>
            <svg style={{ position: 'absolute', top: '18%', left: '45%', width: '10px', height: '10px', opacity: 0.12 }}>
              <path d="M 5 2 C 5 0.8, 6.2 0.2, 7.5 1.4 C 8.8 0.2, 10 0.8, 10 2 C 10 3.8, 7.5 5.8, 7.5 5.8 C 7.5 5.8, 5 3.8, 5 2" fill="#8B4545" />
            </svg>

            {/* ===== QUILL AND INKWELL ===== */}
            <svg viewBox="0 0 80 120" style={{ position: 'absolute', bottom: '25px', left: '25px', width: '55px', height: '80px', opacity: 0.22 }}>
              <ellipse cx="25" cy="105" rx="20" ry="8" fill="#3d2817" />
              <path d="M 8 105 L 8 85 Q 8 80, 15 80 L 35 80 Q 42 80, 42 85 L 42 105" fill="#3d2817" />
              <ellipse cx="25" cy="80" rx="17" ry="6" fill="#1a0f08" />
              <path d="M 55 10 Q 30 40, 22 75" fill="none" stroke="#8B7355" strokeWidth="2" />
              <path d="M 55 10 Q 65 15, 70 5 Q 60 8, 55 10" fill="#d4c5b0" stroke="#8B7355" strokeWidth="1" />
              <path d="M 55 10 Q 58 20, 52 30 Q 48 20, 55 10" fill="#d4c5b0" stroke="#8B7355" strokeWidth="0.5" />
              <path d="M 50 25 Q 45 35, 40 50" fill="none" stroke="#8B7355" strokeWidth="1" opacity="0.5" />
              <path d="M 58 18 Q 62 28, 55 40" fill="none" stroke="#8B7355" strokeWidth="1" opacity="0.5" />
            </svg>

            {/* ===== SMALL FLOWERS scattered ===== */}
            <svg style={{ position: 'absolute', top: '40%', right: '3%', width: '25px', height: '25px', opacity: 0.2 }}>
              <circle cx="12.5" cy="8" r="4" fill="#d4a0b8" />
              <circle cx="8" cy="14" r="4" fill="#d4a0b8" />
              <circle cx="17" cy="14" r="4" fill="#d4a0b8" />
              <circle cx="10" cy="20" r="4" fill="#d4a0b8" />
              <circle cx="15" cy="20" r="4" fill="#d4a0b8" />
              <circle cx="12.5" cy="14" r="3" fill="#e8c060" />
            </svg>
            <svg style={{ position: 'absolute', bottom: '45%', left: '3%', width: '20px', height: '20px', opacity: 0.18 }}>
              <circle cx="10" cy="6" r="3" fill="#a0b8d4" />
              <circle cx="6" cy="11" r="3" fill="#a0b8d4" />
              <circle cx="14" cy="11" r="3" fill="#a0b8d4" />
              <circle cx="8" cy="16" r="3" fill="#a0b8d4" />
              <circle cx="12" cy="16" r="3" fill="#a0b8d4" />
              <circle cx="10" cy="11" r="2.5" fill="#e8d060" />
            </svg>

            {/* ===== BUTTERFLIES ===== */}
            <svg viewBox="0 0 40 30" style={{ position: 'absolute', top: '25%', left: '6%', width: '35px', height: '26px', opacity: 0.2 }}>
              <ellipse cx="20" cy="15" rx="2" ry="8" fill="#5C4033" />
              <ellipse cx="12" cy="10" rx="8" ry="6" fill="#d4a0b8" opacity="0.8" />
              <ellipse cx="28" cy="10" rx="8" ry="6" fill="#d4a0b8" opacity="0.8" />
              <ellipse cx="13" cy="20" rx="6" ry="4" fill="#c490a8" opacity="0.8" />
              <ellipse cx="27" cy="20" rx="6" ry="4" fill="#c490a8" opacity="0.8" />
              <circle cx="10" cy="9" r="2" fill="#8B4545" opacity="0.5" />
              <circle cx="30" cy="9" r="2" fill="#8B4545" opacity="0.5" />
              <path d="M 18 7 Q 16 3, 14 2 M 22 7 Q 24 3, 26 2" stroke="#5C4033" strokeWidth="1" fill="none" />
            </svg>
            <svg viewBox="0 0 35 25" style={{ position: 'absolute', bottom: '30%', right: '10%', width: '28px', height: '20px', opacity: 0.15 }}>
              <ellipse cx="17.5" cy="12.5" rx="1.5" ry="6" fill="#5C4033" />
              <ellipse cx="11" cy="9" rx="6" ry="5" fill="#a0c4b8" opacity="0.8" />
              <ellipse cx="24" cy="9" rx="6" ry="5" fill="#a0c4b8" opacity="0.8" />
              <ellipse cx="12" cy="17" rx="4.5" ry="3" fill="#90b4a8" opacity="0.8" />
              <ellipse cx="23" cy="17" rx="4.5" ry="3" fill="#90b4a8" opacity="0.8" />
              <path d="M 16 6 Q 14 3, 12 2 M 19 6 Q 21 3, 23 2" stroke="#5C4033" strokeWidth="0.8" fill="none" />
            </svg>

            <button className="back-button letter-back-btn" onClick={() => setCurrentPage('menu')}>
              ← Back to Journal
            </button>

            <div className="letter-parchment">
              <textarea
                placeholder={`Dear ${partnerName || '______'},`}
                value={newEntry.content}
                onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
                className="letter-textarea-parchment"
              />

              <div className="parchment-footer">
                <button
                  className="letter-continue-seal-btn"
                  onClick={() => setCurrentPage('signature')}
                  disabled={!newEntry.content.trim()}
                >
                  Continue to Sign
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Signature Page - Parchment Theme */}
        {currentPage === 'signature' && (
          <div className="letter-writing-container parchment-bg">
            {/* ===== CORNER VINES WITH FLOWERS ===== */}
            <svg viewBox="0 0 150 150" style={{ position: 'absolute', top: '10px', left: '10px', width: '100px', height: '100px', opacity: 0.3 }}>
              <path d="M 5 80 Q 5 40, 25 25 Q 40 10, 80 5" fill="none" stroke="#5C4033" strokeWidth="2" />
              <path d="M 10 70 Q 15 50, 30 35 Q 45 20, 70 15" fill="none" stroke="#6B5344" strokeWidth="1.5" />
              <ellipse cx="20" cy="55" rx="8" ry="4" fill="#7a9a6a" transform="rotate(-45 20 55)" />
              <ellipse cx="35" cy="35" rx="7" ry="3.5" fill="#8aaa7a" transform="rotate(-30 35 35)" />
              <ellipse cx="55" cy="22" rx="6" ry="3" fill="#7a9a6a" transform="rotate(-15 55 22)" />
              <circle cx="45" cy="45" r="5" fill="#b8a0c4" opacity="0.7" />
              <circle cx="45" cy="45" r="2" fill="#9a7aaa" />
            </svg>
            <svg viewBox="0 0 150 150" style={{ position: 'absolute', top: '10px', right: '10px', width: '100px', height: '100px', opacity: 0.3, transform: 'scaleX(-1)' }}>
              <path d="M 5 80 Q 5 40, 25 25 Q 40 10, 80 5" fill="none" stroke="#5C4033" strokeWidth="2" />
              <path d="M 10 70 Q 15 50, 30 35 Q 45 20, 70 15" fill="none" stroke="#6B5344" strokeWidth="1.5" />
              <ellipse cx="20" cy="55" rx="8" ry="4" fill="#7a9a6a" transform="rotate(-45 20 55)" />
              <ellipse cx="35" cy="35" rx="7" ry="3.5" fill="#8aaa7a" transform="rotate(-30 35 35)" />
              <ellipse cx="55" cy="22" rx="6" ry="3" fill="#7a9a6a" transform="rotate(-15 55 22)" />
              <circle cx="28" cy="50" r="4" fill="#d4a0a0" opacity="0.7" />
              <circle cx="28" cy="50" r="1.5" fill="#c4877a" />
            </svg>
            <svg viewBox="0 0 150 150" style={{ position: 'absolute', bottom: '10px', left: '10px', width: '100px', height: '100px', opacity: 0.3, transform: 'scaleY(-1)' }}>
              <path d="M 5 80 Q 5 40, 25 25 Q 40 10, 80 5" fill="none" stroke="#5C4033" strokeWidth="2" />
              <path d="M 10 70 Q 15 50, 30 35 Q 45 20, 70 15" fill="none" stroke="#6B5344" strokeWidth="1.5" />
              <ellipse cx="20" cy="55" rx="8" ry="4" fill="#7a9a6a" transform="rotate(-45 20 55)" />
              <ellipse cx="35" cy="35" rx="7" ry="3.5" fill="#8aaa7a" transform="rotate(-30 35 35)" />
              <ellipse cx="55" cy="22" rx="6" ry="3" fill="#7a9a6a" transform="rotate(-15 55 22)" />
              <circle cx="50" cy="35" r="4.5" fill="#a0c4b8" opacity="0.7" />
              <circle cx="50" cy="35" r="1.8" fill="#7aaa9a" />
            </svg>
            <svg viewBox="0 0 150 150" style={{ position: 'absolute', bottom: '10px', right: '10px', width: '100px', height: '100px', opacity: 0.3, transform: 'scale(-1)' }}>
              <path d="M 5 80 Q 5 40, 25 25 Q 40 10, 80 5" fill="none" stroke="#5C4033" strokeWidth="2" />
              <path d="M 10 70 Q 15 50, 30 35 Q 45 20, 70 15" fill="none" stroke="#6B5344" strokeWidth="1.5" />
              <ellipse cx="20" cy="55" rx="8" ry="4" fill="#7a9a6a" transform="rotate(-45 20 55)" />
              <ellipse cx="35" cy="35" rx="7" ry="3.5" fill="#8aaa7a" transform="rotate(-30 35 35)" />
              <ellipse cx="55" cy="22" rx="6" ry="3" fill="#7a9a6a" transform="rotate(-15 55 22)" />
              <circle cx="40" cy="28" r="4" fill="#d4a0a0" opacity="0.7" />
              <circle cx="40" cy="28" r="1.5" fill="#c4877a" />
            </svg>

            {/* ===== BIRD ON BRANCH ===== */}
            <svg viewBox="0 0 100 70" style={{ position: 'absolute', top: '18%', left: '6%', width: '70px', height: '50px', opacity: 0.22 }}>
              <path d="M 0 45 Q 25 40, 50 43 Q 75 40, 100 48" fill="none" stroke="#5C4033" strokeWidth="2.5" />
              <path d="M 30 42 Q 25 32, 18 26" fill="none" stroke="#5C4033" strokeWidth="1.2" />
              <ellipse cx="18" cy="24" rx="5" ry="2.5" fill="#7a9a6a" transform="rotate(-20 18 24)" />
              <ellipse cx="55" cy="32" rx="10" ry="8" fill="#7a6a5a" />
              <circle cx="65" cy="27" r="6" fill="#7a6a5a" />
              <path d="M 71 27 L 77 28 L 71 30 Z" fill="#c4a060" />
              <circle cx="67" cy="25" r="1.8" fill="#1a0f08" />
              <ellipse cx="53" cy="32" rx="7" ry="5" fill="#6a5a4a" />
              <path d="M 45 32 L 36 30 L 38 34 L 34 36 L 45 35 Z" fill="#6a5a4a" />
              <path d="M 55 40 L 55 44 M 53 44 L 57 44" stroke="#5C4033" strokeWidth="1.2" />
              <path d="M 60 40 L 60 43 M 58 43 L 62 43" stroke="#5C4033" strokeWidth="1.2" />
            </svg>

            {/* ===== SPARKLES ===== */}
            <svg style={{ position: 'absolute', top: '15%', right: '15%', width: '18px', height: '18px', opacity: 0.32 }}>
              <path d="M 9 0 L 9 18 M 0 9 L 18 9 M 2.5 2.5 L 15.5 15.5 M 15.5 2.5 L 2.5 15.5" stroke="#c4a060" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', top: '40%', right: '8%', width: '15px', height: '15px', opacity: 0.28 }}>
              <path d="M 7.5 0 L 7.5 15 M 0 7.5 L 15 7.5 M 2 2 L 13 13 M 13 2 L 2 13" stroke="#d4a0a0" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', bottom: '35%', left: '10%', width: '14px', height: '14px', opacity: 0.25 }}>
              <path d="M 7 0 L 7 14 M 0 7 L 14 7 M 2 2 L 12 12 M 12 2 L 2 12" stroke="#b8a0c4" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', top: '60%', right: '12%', width: '12px', height: '12px', opacity: 0.22 }}>
              <path d="M 6 0 L 6 12 M 0 6 L 12 6" stroke="#c4a060" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', top: '25%', left: '20%', width: '10px', height: '10px', opacity: 0.25 }}>
              <path d="M 5 0 L 5 10 M 0 5 L 10 5" stroke="#d4b896" strokeWidth="1" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', bottom: '50%', right: '20%', width: '11px', height: '11px', opacity: 0.23 }}>
              <path d="M 5.5 0 L 5.5 11 M 0 5.5 L 11 5.5" stroke="#a0b4c4" strokeWidth="1" strokeLinecap="round" />
            </svg>

            {/* ===== HEARTS ===== */}
            <svg style={{ position: 'absolute', top: '28%', right: '5%', width: '20px', height: '20px', opacity: 0.16 }}>
              <path d="M 10 4 C 10 1.5, 12.5 0.5, 15 3 C 17.5 0.5, 20 1.5, 20 4 C 20 8, 15 12, 15 12 C 15 12, 10 8, 10 4" fill="#8B4545" />
            </svg>
            <svg style={{ position: 'absolute', bottom: '28%', right: '6%', width: '14px', height: '14px', opacity: 0.12 }}>
              <path d="M 7 3 C 7 1.2, 8.8 0.4, 10.5 2 C 12.2 0.4, 14 1.2, 14 3 C 14 5.5, 10.5 8.5, 10.5 8.5 C 10.5 8.5, 7 5.5, 7 3" fill="#9B5555" />
            </svg>
            <svg style={{ position: 'absolute', top: '55%', left: '5%', width: '12px', height: '12px', opacity: 0.1 }}>
              <path d="M 6 2.5 C 6 1, 7.5 0.3, 9 1.7 C 10.5 0.3, 12 1, 12 2.5 C 12 4.5, 9 7, 9 7 C 9 7, 6 4.5, 6 2.5" fill="#8B4545" />
            </svg>

            {/* ===== BUTTERFLY ===== */}
            <svg viewBox="0 0 40 30" style={{ position: 'absolute', bottom: '22%', right: '15%', width: '32px', height: '24px', opacity: 0.18 }}>
              <ellipse cx="20" cy="15" rx="2" ry="8" fill="#5C4033" />
              <ellipse cx="12" cy="10" rx="8" ry="6" fill="#d4a0b8" opacity="0.8" />
              <ellipse cx="28" cy="10" rx="8" ry="6" fill="#d4a0b8" opacity="0.8" />
              <ellipse cx="13" cy="20" rx="6" ry="4" fill="#c490a8" opacity="0.8" />
              <ellipse cx="27" cy="20" rx="6" ry="4" fill="#c490a8" opacity="0.8" />
              <circle cx="10" cy="9" r="2" fill="#8B4545" opacity="0.5" />
              <circle cx="30" cy="9" r="2" fill="#8B4545" opacity="0.5" />
              <path d="M 18 7 Q 16 3, 14 2 M 22 7 Q 24 3, 26 2" stroke="#5C4033" strokeWidth="1" fill="none" />
            </svg>

            {/* ===== SMALL FLOWERS ===== */}
            <svg style={{ position: 'absolute', top: '45%', right: '4%', width: '22px', height: '22px', opacity: 0.18 }}>
              <circle cx="11" cy="7" r="3.5" fill="#a0b8d4" />
              <circle cx="7" cy="12" r="3.5" fill="#a0b8d4" />
              <circle cx="15" cy="12" r="3.5" fill="#a0b8d4" />
              <circle cx="9" cy="17" r="3.5" fill="#a0b8d4" />
              <circle cx="13" cy="17" r="3.5" fill="#a0b8d4" />
              <circle cx="11" cy="12" r="2.5" fill="#e8d060" />
            </svg>
            <svg style={{ position: 'absolute', bottom: '40%', left: '4%', width: '18px', height: '18px', opacity: 0.15 }}>
              <circle cx="9" cy="5" r="3" fill="#d4a0b8" />
              <circle cx="5" cy="10" r="3" fill="#d4a0b8" />
              <circle cx="13" cy="10" r="3" fill="#d4a0b8" />
              <circle cx="7" cy="14" r="3" fill="#d4a0b8" />
              <circle cx="11" cy="14" r="3" fill="#d4a0b8" />
              <circle cx="9" cy="10" r="2" fill="#e8c060" />
            </svg>

            <button className="back-button letter-back-btn" onClick={() => setCurrentPage('letter')}>
              ← Back to Letter
            </button>

            <div className="sealing-parchment">
              <div className="sealing-header">
                <h2 className="sealing-title">Sign Your Letter</h2>
                <p className="sealing-subtitle">
                  Choose how you'd like to sign this letter...
                </p>
              </div>

              <div className="sealing-content">
                {/* Signature Selection */}
                <div className="signature-section">
                  <h4 className="seal-subtitle">Your Signature</h4>
                  <p className="seal-description">
                    How would you like to sign this letter?
                  </p>
                  <div className="signature-options">
                    <button
                      className={`signature-btn ${newEntry.signature === userProfile?.name ? 'selected' : ''}`}
                      onClick={() => setNewEntry({ ...newEntry, signature: userProfile?.name || 'Me' })}
                    >
                      {userProfile?.name || 'Your Name'}
                    </button>
                    <button
                      className={`signature-btn ${newEntry.signature === 'Love' ? 'selected' : ''}`}
                      onClick={() => setNewEntry({ ...newEntry, signature: 'Love' })}
                    >
                      Love
                    </button>
                    <button
                      className={`signature-btn ${newEntry.signature === 'Until next entry' ? 'selected' : ''}`}
                      onClick={() => setNewEntry({ ...newEntry, signature: 'Until next entry' })}
                    >
                      Until next entry
                    </button>
                    <button
                      className={`signature-btn ${newEntry.signature === 'XOXO' ? 'selected' : ''}`}
                      onClick={() => setNewEntry({ ...newEntry, signature: 'XOXO' })}
                    >
                      XOXO
                    </button>
                  </div>
                  <div className="custom-signature">
                    <label className="option-label">Custom:</label>
                    <input
                      type="text"
                      placeholder="Your signature..."
                      value={newEntry.signature}
                      onChange={(e) => setNewEntry({ ...newEntry, signature: e.target.value })}
                      className="signature-input"
                    />
                  </div>
                </div>

                <div className="seal-send-section">
                  <button className="seal-send-button" onClick={() => setCurrentPage('seal')}>
                    Continue to Seal →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sealing Page - Parchment Theme */}
        {currentPage === 'seal' && (
          <div className="letter-writing-container parchment-bg">
            {/* Decorative corner flourishes */}
            <svg className="corner-flourish top-left" viewBox="0 0 100 100" style={{
              position: 'absolute', top: '15px', left: '15px', width: '60px', height: '60px', opacity: 0.25
            }}>
              <path d="M 5 50 Q 5 5, 50 5" fill="none" stroke="#5C4033" strokeWidth="2" />
              <path d="M 15 50 Q 15 15, 50 15" fill="none" stroke="#5C4033" strokeWidth="1.5" />
              <circle cx="8" cy="50" r="3" fill="#5C4033" />
              <circle cx="50" cy="8" r="3" fill="#5C4033" />
            </svg>
            <svg className="corner-flourish top-right" viewBox="0 0 100 100" style={{
              position: 'absolute', top: '15px', right: '15px', width: '60px', height: '60px', opacity: 0.25, transform: 'scaleX(-1)'
            }}>
              <path d="M 5 50 Q 5 5, 50 5" fill="none" stroke="#5C4033" strokeWidth="2" />
              <path d="M 15 50 Q 15 15, 50 15" fill="none" stroke="#5C4033" strokeWidth="1.5" />
              <circle cx="8" cy="50" r="3" fill="#5C4033" />
              <circle cx="50" cy="8" r="3" fill="#5C4033" />
            </svg>
            <svg className="corner-flourish bottom-left" viewBox="0 0 100 100" style={{
              position: 'absolute', bottom: '15px', left: '15px', width: '60px', height: '60px', opacity: 0.25, transform: 'scaleY(-1)'
            }}>
              <path d="M 5 50 Q 5 5, 50 5" fill="none" stroke="#5C4033" strokeWidth="2" />
              <path d="M 15 50 Q 15 15, 50 15" fill="none" stroke="#5C4033" strokeWidth="1.5" />
              <circle cx="8" cy="50" r="3" fill="#5C4033" />
              <circle cx="50" cy="8" r="3" fill="#5C4033" />
            </svg>
            <svg className="corner-flourish bottom-right" viewBox="0 0 100 100" style={{
              position: 'absolute', bottom: '15px', right: '15px', width: '60px', height: '60px', opacity: 0.25, transform: 'scale(-1)'
            }}>
              <path d="M 5 50 Q 5 5, 50 5" fill="none" stroke="#5C4033" strokeWidth="2" />
              <path d="M 15 50 Q 15 15, 50 15" fill="none" stroke="#5C4033" strokeWidth="1.5" />
              <circle cx="8" cy="50" r="3" fill="#5C4033" />
              <circle cx="50" cy="8" r="3" fill="#5C4033" />
            </svg>

            {/* Flying birds */}
            <svg style={{ position: 'absolute', top: '10%', right: '18%', width: '35px', height: '20px', opacity: 0.2 }}>
              <path d="M 0 10 Q 8 5, 17 10 Q 26 5, 35 10" fill="none" stroke="#5C4033" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', top: '16%', right: '25%', width: '25px', height: '15px', opacity: 0.15 }}>
              <path d="M 0 7 Q 6 3, 12 7 Q 18 3, 25 7" fill="none" stroke="#5C4033" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', top: '6%', right: '12%', width: '20px', height: '12px', opacity: 0.12 }}>
              <path d="M 0 6 Q 5 2, 10 6 Q 15 2, 20 6" fill="none" stroke="#5C4033" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', top: '20%', left: '10%', width: '28px', height: '16px', opacity: 0.18 }}>
              <path d="M 0 8 Q 7 3, 14 8 Q 21 3, 28 8" fill="none" stroke="#5C4033" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <svg style={{ position: 'absolute', top: '14%', left: '18%', width: '18px', height: '10px', opacity: 0.12 }}>
              <path d="M 0 5 Q 4 2, 9 5 Q 14 2, 18 5" fill="none" stroke="#5C4033" strokeWidth="1.2" strokeLinecap="round" />
            </svg>

            {/* Small decorative hearts */}
            <svg style={{ position: 'absolute', top: '28%', right: '6%', width: '18px', height: '18px', opacity: 0.15 }}>
              <path d="M 9 3.5 C 9 1, 11.5 0, 13.5 1.8 C 15.5 0, 18 1, 18 3.5 C 18 7, 13.5 10.5, 13.5 10.5 C 13.5 10.5, 9 7, 9 3.5" fill="#8B4545" />
            </svg>
            <svg style={{ position: 'absolute', bottom: '25%', left: '6%', width: '14px', height: '14px', opacity: 0.12 }}>
              <path d="M 7 2.5 C 7 0.5, 9 0, 10.5 1.3 C 12 0, 14 0.5, 14 2.5 C 14 5, 10.5 7.5, 10.5 7.5 C 10.5 7.5, 7 5, 7 2.5" fill="#8B4545" />
            </svg>

            <button className="back-button letter-back-btn" onClick={() => setCurrentPage('signature')}>
              ← Back to Signature
            </button>

            <div className="sealing-parchment">
              <div className="sealing-header">
                <h2 className="sealing-title">Seal Your Letter</h2>
                <p className="sealing-subtitle">
                  Choose when your letter may be opened...
                </p>
              </div>

              <div className="sealing-content">
                <div className="seal-methods-parchment">
                  <button
                    className={`seal-method-btn ${newEntry.sealMethod === 'moonPhase' ? 'active' : ''}`}
                    onClick={() => setNewEntry({ ...newEntry, sealMethod: 'moonPhase' })}
                  >
                    <span className="method-icon">🌙</span>
                    <span className="method-name">Moon Phase</span>
                  </button>

                  <button
                    className={`seal-method-btn ${newEntry.sealMethod === 'timeDelay' ? 'active' : ''}`}
                    onClick={() => setNewEntry({ ...newEntry, sealMethod: 'timeDelay' })}
                  >
                    <span className="method-icon">⏳</span>
                    <span className="method-name">Time Delay</span>
                  </button>
                </div>
                {/* Moon Phase Seal Options */}
                {newEntry.sealMethod === 'moonPhase' && (
                  <div className="seal-options">
                    <h4 className="seal-subtitle">Choose Moon Phase</h4>
                    <p className="seal-description">
                      Letter opens when this moon phase arrives
                    </p>
                    <p className="current-moon">
                      <MoonPhaseIcon phase={currentMoonPhase.name} size={24} />
                      <span>Current: {currentMoonPhase.name}</span>
                    </p>

                    <div className="moon-phase-grid">
                      {MOON_PHASES.map((phase) => {
                        const nextDate = getNextMoonPhaseDate(phase.name);
                        const daysUntil = Math.ceil((nextDate - new Date()) / (1000 * 60 * 60 * 24));

                        return (
                          <button
                            key={phase.name}
                            className={`moon-phase-btn ${newEntry.sealedMoonPhase === phase.name ? 'selected' : ''}`}
                            onClick={() => setNewEntry({ ...newEntry, sealedMoonPhase: phase.name })}
                          >
                            <MoonPhaseIcon phase={phase.name} size={36} />
                            <span className="moon-name">{phase.name}</span>
                            <span className="moon-days">{daysUntil}d</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Time Delay Seal Options */}
                {newEntry.sealMethod === 'timeDelay' && (
                  <div className="seal-options">
                    <h4 className="seal-subtitle">Set Time Delay</h4>
                    <p className="seal-description">
                      Letter opens after this many days
                    </p>

                    <div className="delay-options">
                      {[1, 3, 7, 14, 30].map((days) => (
                        <button
                          key={days}
                          className={`delay-btn ${newEntry.delayDays === days ? 'selected' : ''}`}
                          onClick={() => setNewEntry({ ...newEntry, delayDays: days })}
                        >
                          {days} {days === 1 ? 'day' : 'days'}
                        </button>
                      ))}
                    </div>

                    <div className="custom-delay">
                      <label className="option-label">Custom:</label>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={newEntry.delayDays}
                        onChange={(e) => setNewEntry({ ...newEntry, delayDays: parseInt(e.target.value) || 1 })}
                        className="delay-input"
                      />
                      <span className="delay-label">days</span>
                    </div>
                  </div>
                )}

                <div className="seal-send-section">
                  <button className="seal-send-button" onClick={handleSaveEntry}>
                    {newEntry.sealMethod === 'moonPhase' && (
                      <>
                        <MoonPhaseIcon phase={newEntry.sealedMoonPhase} size={28} />
                        <span>Seal & Send Letter</span>
                      </>
                    )}
                    {newEntry.sealMethod === 'timeDelay' && (
                      <>
                        <span>⏳</span>
                        <span>Seal & Send ({newEntry.delayDays} days)</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Journal Entry Writing Page */}
        {currentPage === 'journalEntry' && (
          <>
            <div className="journal-left-page">
              <div className="page-content">
                <button className="back-button" onClick={() => setCurrentPage('menu')}>
                  ← Back
                </button>
                <h3 className="page-title">Journal Entry</h3>
                <div className="writing-options">
                  <label className="option-label">Title:</label>
                  <input
                    type="text"
                    placeholder="Entry title..."
                    value={newEntry.title}
                    onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })}
                    className="title-input"
                  />
                </div>

                {/* Decorative flavor text */}
                <div className="journal-flavor-text" style={{
                  marginTop: '50px',
                  padding: '20px',
                  opacity: 0.5,
                  fontFamily: 'var(--font-handwriting)',
                  fontSize: '1.1rem',
                  color: 'var(--color-deep-brown)',
                  lineHeight: '2',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}>
                  <p>From flower crowns</p>
                  <p>to our nightgowns,</p>
                  <p>we journal together</p>
                  <p>expressing with ink</p>
                  <p>strenghting our link</p>
                </div>
              </div>
            </div>

            <div className="journal-right-page">
              <div className="page-content">
                <textarea
                  placeholder="Today..."
                  value={newEntry.content}
                  onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
                  className="journal-textarea"
                />
                <button className="send-button" onClick={handleSaveEntry}>
                  Save Entry
                </button>
              </div>
            </div>
          </>
        )}

        {/* Read Journal Page */}
        {currentPage === 'readJournal' && (
          <>
            {journalEntries.length === 0 ? (
              <>
                <div className="journal-left-page">
                  <div className="page-content">
                    <button className="back-button" onClick={() => setCurrentPage('menu')}>
                      ← Back
                    </button>
                    <h3 className="page-title">Journal</h3>
                  </div>
                </div>
                <div className="journal-right-page">
                  <div className="page-content">
                    <p className="no-entries-message">Our journal is waiting for your first entry...</p>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Left Page - Current Entry */}
                <div className="journal-left-page journal-reading-page">
                  <div className="page-content journal-entry-content">
                    <button className="back-button" onClick={() => setCurrentPage('menu')}>
                      ← Back
                    </button>

                    <div className="journal-entry-header">
                      <h3 className="journal-entry-title">{journalEntries[currentEntryIndex]?.title}</h3>
                      <p className="journal-entry-date">
                        {new Date(journalEntries[currentEntryIndex]?.createdAt).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    </div>

                    <div className="journal-entry-body">
                      <p className="journal-entry-text">
                        {journalEntries[currentEntryIndex]?.content}
                      </p>
                    </div>

                    <p className="journal-entry-author">- {journalEntries[currentEntryIndex]?.author}</p>
                  </div>
                </div>

                {/* Right Page - Navigation */}
                <div className="journal-right-page journal-reading-page">
                  <div className="page-content">
                    <div className="journal-nav">
                      <button
                        className="journal-nav-btn"
                        onClick={() => setCurrentEntryIndex(Math.max(0, currentEntryIndex - 1))}
                        disabled={currentEntryIndex === 0}
                      >
                        ← Previous Entry
                      </button>

                      <div className="journal-page-number">
                        Entry {currentEntryIndex + 1} of {journalEntries.length}
                      </div>

                      <button
                        className="journal-nav-btn"
                        onClick={() => setCurrentEntryIndex(Math.min(journalEntries.length - 1, currentEntryIndex + 1))}
                        disabled={currentEntryIndex === journalEntries.length - 1}
                      >
                        Next Entry →
                      </button>
                    </div>

                    {/* Entry List Preview */}
                    <div className="journal-entries-sidebar">
                      <h4 className="sidebar-title">All Entries</h4>
                      <div className="entries-list-scroll">
                        {journalEntries.map((entry, index) => (
                          <button
                            key={entry.id}
                            className={`entry-list-item ${index === currentEntryIndex ? 'active' : ''}`}
                            onClick={() => setCurrentEntryIndex(index)}
                          >
                            <span className="entry-list-title">{entry.title}</span>
                            <span className="entry-list-date">
                              {new Date(entry.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                              })}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
};

export default Journal;
